import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import { ChatConversation, ChatDraft, emptyChatDraft } from 'services/api/chat';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import ChannelRail from './ChannelRail';
import ChannelSubscriber from './ChannelSubscriber';
import ChannelView from './ChannelView';

// Exported for InboundReferralPage: the tab-badge rollup excludes the actively-viewed
// channel (resolved from this param), so a watched channel can never flash the tab badge.
export const CHANNEL_PARAM = 'channel';

type Props = {
  // Fed from the page-level useChatConversations mount so a later story's tab badge and
  // this tab read one cache.
  conversations: ChatConversation[];
  isLoading: boolean;
  isError: boolean;
  // Terminal (completed/canceled) referral → the composer is read-only in every channel.
  isReadOnly: boolean;
  // Per-conversation draft map, owned by the PAGE: this tab unmounts on tab switches, so
  // drafts held here would die on a trip to the Activity Log.
  drafts: Record<string, ChatDraft>;
  onDraftChange: (conversationId: string, value: ChatDraft) => void;
};

// The Chat tab: a facility rail (multi-facility only) beside the selected channel's view.
// Selection is the `?channel=<conversationId>` URL param (deep-linkable, survives refresh);
// the derived default is the first channel.
export default function ChatTab({ conversations, isLoading, isError, isReadOnly, drafts, onDraftChange }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Alphabetical is the canonical order (matches the rail); the "first channel" default is
  // the top of it — so a single-facility user lands on their one channel.
  const sorted = useMemo(
    () => [...conversations].sort((a, b) => a.audience.name.localeCompare(b.audience.name)),
    [conversations]
  );

  const channelParam = searchParams.get(CHANNEL_PARAM);
  const selected = sorted.find((conversation) => conversation.id === channelParam) ?? sorted[0];

  // Canonicalize the URL to the resolved channel (absent or stale param → default first),
  // so the selection is deep-linkable and survives refresh without adding history entries.
  useEffect(() => {
    if (!selected || channelParam === selected.id) return;
    const next = new URLSearchParams(searchParams);
    next.set(CHANNEL_PARAM, selected.id);
    setSearchParams(next, { replace: true });
  }, [selected, channelParam, searchParams, setSearchParams]);

  const onSelect = (conversationId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(CHANNEL_PARAM, conversationId);
    setSearchParams(next);
  };

  if (isLoading)
    return (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  if (isError) return <Muted>We couldn’t load this referral’s chat.</Muted>;
  // Zero-channel referrals are a tolerated state: referrals created before the chat
  // feature existed have no conversations (deliberately no backfill), and they age out
  // as those referrals close. Every referral created since gets its channels at creation.
  if (!selected) return <Muted data-testid='chat-no-channels'>No chat channels for this referral.</Muted>;

  const showRail = sorted.length > 1;

  return (
    <Layout data-testid='chat-tab'>
      {/* The open channel's live message stream. Keyed by conversation — mandatory: not for the
          subscription itself (the hook re-subscribes on param changes), but to reset the
          handshake-skip refs so a facility switch doesn't fire a redundant refetch. */}
      <ChannelSubscriber key={`subscriber-${selected.id}`} conversationId={selected.id} />
      {showRail && (
        <RailColumn>
          <ChannelRail conversations={sorted} selectedId={selected.id} onSelect={onSelect} />
        </RailColumn>
      )}
      <ChannelView
        // Key by conversation so message-list/composer state resets cleanly on channel switch.
        key={selected.id}
        conversation={selected}
        showFacilityHeader={showRail}
        isReadOnly={isReadOnly}
        // The broadcast toggle rides the same multi-facility condition as the rail.
        canBroadcast={showRail}
        draft={drafts[selected.id] ?? emptyChatDraft}
        onDraftChange={(value) => onDraftChange(selected.id, value)}
      />
    </Layout>
  );
}

// Fill Page.Content exactly (it's position: relative and the page's scroll area): the tab
// owns its full height, the message list scrolls internally, and the composer stays pinned
// to the bottom of the screen. Absolutely positioned — a height: 100% child would add
// Page.Content's margin-top/padding-bottom back as page scroll under the composer — with
// the inset mirroring that spacing (24px sides/bottom).
const Layout = styled.div`
  position: absolute;
  inset: 0 24px 24px;
  display: flex;
  gap: 24px;
  align-items: stretch;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const RailColumn = styled.div`
  flex: 0 0 240px;
  max-width: 240px;
  border-right: 1px solid ${colors.black10};
  padding-right: 20px;
  overflow-y: auto;

  @media (max-width: 900px) {
    flex: 0 1 auto;
    max-height: 40%;
    max-width: none;
    width: 100%;
    border-right: none;
    border-bottom: 1px solid ${colors.black10};
    padding-right: 0;
    padding-bottom: 16px;
  }
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;
